/**
 * Abrechnungswege und Abrechnungsstand eines Monats (0039). Dieselbe Regel wie app.period_needs und
 * app.sync_period_status in der Datenbank:
 *
 *   über die Agentur   Kundenseite: Agentur → Marke. Creator-Seite: Creator → Agentur (bezahlt = ausgezahlt);
 *                      entfällt bei „Nur Provision“ (die Marke zahlt den Creator selbst).
 *   direkt an die Marke Kundenseite: Creator → Marke. Creator-Seite: Agentur → Creator (Provision, Gebühr);
 *                      entfällt ohne Agenturanteil.
 *
 * Bezahlt ist ein Monat erst, wenn jede nötige Seite bezahlt ist. „Verbucht“ heißt überall: Geld der Marke ist
 * eingegangen (Kundenseite bezahlt) oder der Monat wurde von Hand gebucht.
 */
import type { IsoDate } from "./types.js";

export type InvoiceRoute = "via_agency" | "direct_to_brand";
export type InvoiceKindKey = "talent_to_agency" | "agency_to_brand" | "talent_to_brand" | "agency_to_talent";

export const INVOICE_ROUTES: readonly InvoiceRoute[] = ["via_agency", "direct_to_brand"];

/** Wirksamer Weg: Kooperation vor Marke vor Agentur; ohne Angabe über die Agentur. */
export function effectiveRoute(...candidates: readonly (string | null | undefined)[]): InvoiceRoute {
  for (const c of candidates) if (c === "via_agency" || c === "direct_to_brand") return c;
  return "via_agency";
}

/** Rechnung an die Marke (Kundenseite), gleich wer sie ausstellt. */
export const isToBrand = (kind: string): boolean => kind === "agency_to_brand" || kind === "talent_to_brand";
/** Vom Creator ausgestellt (an die Agentur oder direkt an die Marke). */
export const isFromCreator = (kind: string): boolean => kind === "talent_to_agency" || kind === "talent_to_brand";
/** Von der Agentur ausgestellt (an die Marke oder an den Creator). */
export const isFromAgency = (kind: string): boolean => kind === "agency_to_brand" || kind === "agency_to_talent";

const NO_INVOICE_MODELS = new Set(["event", "gifting"]);

export interface SettlementKinds {
  route: InvoiceRoute;
  /** Rechnung an die Marke */
  customer: InvoiceKindKey;
  /** Ausgleich zwischen Agentur und Creator */
  creator: InvoiceKindKey;
  /** Die Rechnung, die der Creator für den Monat stellt (Anforderung) */
  creatorDoc: InvoiceKindKey;
  needCustomer: boolean;
  needCreator: boolean;
}

/** selfEmployed: Creator-Konto (0178), das Konto stellt selbst an die Marke; eine Creator-Seite gibt es nicht (wie app.period_needs) */
export function settlementKinds(input: { route: InvoiceRoute; model: string; budgetCents?: number | null; agencyShare: boolean; selfEmployed?: boolean }): SettlementKinds {
  const direct = input.route === "direct_to_brand";
  const noInvoice = NO_INVOICE_MODELS.has(input.model) || (input.model === "barter" && !(input.budgetCents ?? 0));
  return {
    route: input.route,
    customer: direct ? "talent_to_brand" : "agency_to_brand",
    creator: direct ? "agency_to_talent" : "talent_to_agency",
    creatorDoc: direct ? "talent_to_brand" : "talent_to_agency",
    needCustomer: !noInvoice,
    needCreator: noInvoice || input.selfEmployed ? false : direct ? input.agencyShare : input.model !== "commission_only",
  };
}

/** written_off: als uneinbringlich ausgebucht (Forderungsausfall); steht auf „bezahlt“, Geld kam aber nie (GELD-5, GELD-19) */
export type SideState = "not_needed" | "none" | "invoiced" | "paid" | "written_off";

export interface SettlementInvoice {
  kind: string;
  /** Datenbank-Status (draft … discarded) */
  status: string;
  paidAt?: string | null;
  /** Als uneinbringlich ausgebucht (bad_debt_at): kein Geldeingang, zählt nie als bezahlt oder verbucht */
  badDebt?: boolean;
  /** Stornorechnung (credit_for): hebt ihre Rechnung auf und rechnet selbst nichts ab (Praxistest K-138) */
  credit?: boolean;
}

export interface MonthSettlement {
  kinds: SettlementKinds;
  customer: SideState;
  creator: SideState;
  /** Wie deal_periods.status in der Datenbank */
  status: "open" | "invoice_requested" | "invoiced" | "paid" | "cancelled";
  customerPaidAt: IsoDate | null;
  creatorPaidAt: IsoDate | null;
  /** Geld der Marke eingegangen oder von Hand gebucht */
  booked: boolean;
  /** Die Rechnung des Creators kann (noch) angefordert werden */
  canRequest: boolean;
}

const LIVE = new Set(["submitted", "approved", "sent", "paid"]);

const reallyPaid = (i: SettlementInvoice) => i.status === "paid" && !i.badDebt;

function side(list: readonly SettlementInvoice[], kind: string, needed: boolean): SideState {
  if (!needed) return "not_needed";
  const mine = list.filter((i) => i.kind === kind);
  if (mine.some(reallyPaid)) return "paid";
  if (mine.some((i) => LIVE.has(i.status) && i.status !== "paid")) return "invoiced";
  if (mine.some((i) => i.status === "paid" && i.badDebt)) return "written_off";
  return "none";
}

export function deriveSettlement(input: {
  kinds: SettlementKinds;
  invoices: readonly SettlementInvoice[];
  invoiceRequestedAt?: string | null;
  cancelled?: boolean;
  manualBookedAt?: string | null;
  /**
   * Stand aus dem bisherigen System (deal_periods.imported_state, wie app.sync_period_status): deckt die Seite, zu der es im Portal
   * keine Rechnung gibt. „invoiced“ = dort abgerechnet, „brand_paid“ = Marke hat gezahlt (Creator offen), „paid“ = beide erledigt.
   */
  imported?: "invoiced" | "brand_paid" | "paid" | null;
}): MonthSettlement {
  const k = input.kinds;
  const imp = input.imported ?? null;
  const invoices = input.invoices.filter((i) => !i.credit);
  const ownCustomer = side(invoices, k.customer, k.needCustomer);
  const ownCreator = side(invoices, k.creator, k.needCreator);
  const customer: SideState = imp && ownCustomer === "none" ? (imp === "invoiced" ? "invoiced" : "paid") : ownCustomer;
  const creator: SideState = imp && ownCreator === "none" ? (imp === "paid" ? "paid" : "invoiced") : ownCreator;
  const paidAt = (kind: string) => invoices.filter((i) => i.kind === kind && reallyPaid(i)).map((i) => (i.paidAt ?? "").slice(0, 10)).filter(Boolean).sort()[0] ?? null;
  const anyLive = invoices.some((i) => LIVE.has(i.status));
  const needed = k.needCustomer || k.needCreator;
  // Eine ausgebuchte Seite ist erledigt (es kommt nichts mehr), aber nie „verbucht“
  const closed = (x: SideState) => x === "paid" || x === "not_needed" || x === "written_off";
  const status: MonthSettlement["status"] = input.cancelled ? "cancelled"
    : needed && closed(customer) && closed(creator) ? "paid"
      : anyLive || imp ? "invoiced"
        : input.invoiceRequestedAt ? "invoice_requested" : "open";
  const creatorDocPaid = invoices.some((i) => i.kind === k.creatorDoc && reallyPaid(i));
  return {
    kinds: k, customer, creator, status,
    customerPaidAt: customer === "paid" ? paidAt(k.customer) ?? (imp && ownCustomer === "none" ? input.manualBookedAt?.slice(0, 10) ?? null : null) : null,
    creatorPaidAt: creator === "paid" ? paidAt(k.creator) : null,
    booked: !!input.manualBookedAt || customer === "paid",
    // Übernommene Monate fordert das Portal nicht an (die Rechnung des Creators liegt im bisherigen System)
    canRequest: !input.cancelled && needed && !creatorDocPaid && !imp,
  };
}

// ---------------------------------------------------------------------------
// Rechnungsempfänger (Marke oder vertretende Agentur), wie app.brand_party
// ---------------------------------------------------------------------------

export interface BrandLike {
  id: string;
  name: string;
  legal_name?: string | null;
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  vat_id?: string | null;
  register_info?: string | null;
  invoice_email?: string | null;
  represented_by?: string | null;
  invoice_via_agency?: boolean | null;
}
export interface DealBillingLike {
  billing_legal_name?: string | null;
  billing_street?: string | null;
  billing_postal_code?: string | null;
  billing_city?: string | null;
  billing_country?: string | null;
  billing_vat_id?: string | null;
  billing_register?: string | null;
  billing_email?: string | null;
  billing_reference?: string | null;
}
export interface BillingParty {
  name: string;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  vatId: string | null;
  register: string | null;
  email: string | null;
  reference: string | null;
  /** Rechnung geht an die vertretende Agentur „für“ diese Marke */
  onBehalfOf: string | null;
  /** Empfänger ist die vertretende Agentur der Marke */
  viaAgencyId: string | null;
}

const t = (s: string | null | undefined): string | null => (s && s.trim() ? s.trim() : null);

/** Rechnungsempfänger: Angaben der Kooperation vor der vertretenden Agentur (falls „Rechnung an die Agentur“) vor der Marke. */
export function billingPartyOf(brand: BrandLike | undefined, brands: readonly BrandLike[], deal?: DealBillingLike | null): BillingParty | null {
  if (!brand) return null;
  const agency = brand.invoice_via_agency && brand.represented_by ? brands.find((b) => b.id === brand.represented_by) : undefined;
  const r = agency ?? brand;
  const d = deal ?? {};
  const ownName = t(d.billing_legal_name);
  return {
    name: ownName ?? t(r.legal_name) ?? r.name,
    street: t(d.billing_street) ?? t(r.street),
    postalCode: t(d.billing_postal_code) ?? t(r.postal_code),
    city: t(d.billing_city) ?? t(r.city),
    country: t(d.billing_country) ?? t(r.country),
    vatId: t(d.billing_vat_id) ?? t(r.vat_id),
    register: t(d.billing_register) ?? t(r.register_info),
    email: t(d.billing_email) ?? t(r.invoice_email),
    reference: t(d.billing_reference),
    onBehalfOf: agency && !ownName ? t(brand.legal_name) ?? brand.name : null,
    viaAgencyId: agency ? agency.id : null,
  };
}
